import React from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useAuth } from "@/context/AuthContext";
import { COMPLAINT_CATEGORIES, breadcrumbLd } from "@/lib/siteContent";
import { Button } from "@/components/ui/button";

const crumbs = [{ label: "Home", to: "/" }, { label: "File a Complaint", to: "/file-a-complaint" }];

export default function FileComplaintLanding() {
  const { user, openAuth, setShowPartyPick } = useAuth();
  const start = () => (user ? setShowPartyPick(true) : openAuth("signup"));

  return (
    <div className="ca-container py-12 max-w-3xl">
      <Seo title="File a Customer or Vendor Complaint Online | ComplaintAdvisor"
        description="Document a customer or vendor complaint with clear facts, transaction details, supporting evidence, and an opportunity for the concerned party to respond."
        path="/file-a-complaint" jsonld={breadcrumbLd(crumbs)} />
      <Breadcrumbs items={crumbs} />
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-3 flex items-center gap-2"><FileText className="h-7 w-7 text-teal" /> File a Complaint</h1>
      <p className="text-slate-700 mt-3 leading-relaxed">
        Use this workflow to document a customer, vendor, service, payment, product, warranty, delivery, or contract-related
        issue. Describe what happened in chronological order and keep the description factual.
      </p>

      <Button onClick={start} className="mt-5 bg-teal-brand hover:bg-teal-700 text-white" data-testid="file-complaint-start">Start Complaint <ArrowRight className="h-4 w-4 ml-2" /></Button>

      <h2 className="font-display text-lg font-bold text-slate-900 mt-10">Complaint categories</h2>
      <div className="mt-3 flex flex-wrap gap-2">
        {COMPLAINT_CATEGORIES.map((c) => <span key={c} className="rounded-full bg-slate-100 text-slate-600 px-3 py-1 text-xs">{c}</span>)}
      </div>

      <h2 className="font-display text-lg font-bold text-slate-900 mt-10">What to include</h2>
      <p className="text-slate-600 mt-2">
        Include dates, invoices, agreed terms, payment details, delivery information, and communication history where relevant.
        Do not include passwords, complete payment-card details, government identification numbers, medical records, or unnecessary sensitive information.
      </p>

      <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
        Submitting a complaint does not establish that the allegation is true. The concerned party may be invited to respond or
        challenge the complaint. Complaint visibility and retention are subject to moderation, policy, legal requirements, and privacy protections.
      </div>

      <p className="mt-6 text-sm text-slate-500">
        Not sure yet? <Link to="/check-before-you-trust" className="text-teal">Check available information first</Link> or{" "}
        <Link to="/resolve-a-dispute" className="text-teal">explore dispute-resolution options</Link>.
      </p>
    </div>
  );
}
